import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import EndorsementsApi from '../../../api/EndorsementsApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import {
	AutofillOperations,
	AutofillOperationTypes,
} from '../../../api/IntegrationsApi';
import RenewalsApi from '../../../api/RenewalsApi';
import { AuthData } from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { ErrorData } from '../../../context/ErrorProvider';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { CLONE_ERROR_REGEX } from '../../../data/constants';
import {
	ACCEPT_RENEWAL_CONFLICT_MSG,
	ACCEPT_RENEWAL_LABEL,
	ACCEPT_RENEWAL_MSG,
	CLONE_RENEWAL_LABEL,
	CLONE_RENEWAL_UNSAVED_CHANGES_CANCEL_TEXT,
	CLONE_RENEWAL_UNSAVED_CHANGES_CONFIRM_TEXT,
	CLONE_RENEWAL_UNSAVED_CHANGES_MSG,
	DISCARD_RENEWAL_LABEL,
	DISCARD_RENEWAL_MSG,
	INVALIDATE_RENEWAL_CONFLICT_MSG,
	INVALIDATE_RENEWAL_LABEL,
	INVALIDATE_RENEWAL_MSG,
	ISSUE_RENEWAL_LABEL,
	ISSUE_RENEWAL_MSG,
	PROCEED_MSG,
	QUOTE_RENEWAL_LABEL,
	QUOTE_RENEWAL_MSG,
	RENEWAL_AUTOFILL_UPDATE_FAILURE,
	RENEWAL_AUTOFILL_UPDATE_SUCCESS,
	RENEWAL_CLONE_CONFLICT_FAILURE,
	RENEWAL_CLONE_CONFLICT_UNKNOWN_FAILURE,
	RENEWAL_CLONE_SUCCESS,
	RENEWAL_UPDATE_FAILURE,
	RENEWAL_UPDATE_SUCCESS,
} from '../../../data/labelsAndMessages';
import { EndorsementResponse } from '../../../types/endorsements';
import { RenewalResponse } from '../../../types/renewals';
import { isAutofillConfigured } from '../../../util/util';
import DropdownButton, { HiddenActionItem } from '../../DropdownButton';
import { errorToast, successToast } from '../../Toast';
import ActionConfirmationModal from '../ActionConfirmationModal';
import RunAutofillButtonAndModal from '../RunAutofillButtonModal';
import { renewalHasUnsavedChanges } from '../util_sharedEndorsementAndRenewal';

interface Props {
	renewalData: RenewalData;
	authData: AuthData;
	errorData: ErrorData;
}

const RenewalActions = (props: Props) => {
	const configData = useConfigContext();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields, isDirty } = formState;
	const { authData, renewalData, errorData } = props;
	const hasPermission = authData.hasPermission;
	const {
		renewalUpdateObj,
		saveFastForm,
		quoteRenewal,
		acceptRenewal,
		issueRenewal,
		invalidateRenewal,
		discardRenewal,
		policy,
		setLoading,
		cloneRenewal,
	} = renewalData;
	const { state } = renewalUpdateObj;
	const navigate = useNavigate();

	const { data: renewals } = useQuery({
		queryKey: ['renewals', policy.locator, authData.auth.authorizationToken],
		queryFn: () =>
			RenewalsApi.getRenewalsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	const { data: endorsements } = useQuery({
		queryKey: [
			'endorsements',
			policy.locator,
			authData.auth.authorizationToken,
		],
		enabled: authData.hasPermission('endorsement.fetch'),
		queryFn: () =>
			EndorsementsApi.getEndorsementsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	const quotedRenewals = renewals?.data.filter(
		(renewal: RenewalResponse) =>
			renewal.state === 'quoted' &&
			renewal.locator !== renewalUpdateObj.locator,
	);
	const quotedEndorsements = endorsements?.data.filter(
		(endorsement: EndorsementResponse) => endorsement.state === 'quoted',
	);

	const willInvalidateOtherQuotes =
		quotedEndorsements?.length > 0 || quotedRenewals?.length > 0;

	// https://socotra.atlassian.net/browse/SOCA-7817
	// If a user does not have access to the endorsement.fetch permission, there will be a chance other quotes will be invalidated (but we don't know for sure)
	const mayInvalidateOtherQuotes =
		!willInvalidateOtherQuotes && !authData.hasPermission('endorsement.fetch');

	const hasUnsavedChanges = renewalHasUnsavedChanges(
		renewalData.renewalResponse,
		renewalUpdateObj,
		isDirty,
		dirtyFields,
		renewalData.productConfig,
		policy,
	);
	const autofillEnabled = isAutofillConfigured(
		policy.productName,
		AutofillOperations.RENEWAL,
		AutofillOperationTypes.UPDATE,
		configData?.dataAutofillSequences ?? [],
	);

	const onUpdate = async () => {
		try {
			setLoading(true);
			await saveFastForm(getValues(), dirtyFields, reset, {
				runAutofill: false,
			});
			successToast(RENEWAL_UPDATE_SUCCESS());
		} catch (error) {
			errorToast(RENEWAL_UPDATE_FAILURE());
			errorData.handleErrorResponse(error, 'Failed to save');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const onRunAutofill = async () => {
		try {
			setLoading(true);
			await saveFastForm(getValues(), dirtyFields, reset, {
				runAutofill: true,
			});
			successToast(RENEWAL_AUTOFILL_UPDATE_SUCCESS());
		} catch (e) {
			errorToast(RENEWAL_AUTOFILL_UPDATE_FAILURE());
		} finally {
			setLoading(false);
		}
	};

	const onQuote = async () => {
		let success = false;
		try {
			success = await quoteRenewal(getValues(), dirtyFields, reset, {});
		} catch (e) {
			setLoading(false);
		}
		if (success) {
			navigate(
				`/policy/${renewalUpdateObj.policyLocator}/renewals/${renewalUpdateObj.locator}/price-breakdown`,
			);
		}
	};

	const onAccept = async (isDraft?: boolean) => {
		let success = false;
		try {
			success = await acceptRenewal(
				getValues(),
				dirtyFields,
				reset,
				{},
				isDraft,
			);
		} catch (e) {
			setLoading(false);
		}
		if (success) {
			navigate(
				`/policy/${renewalUpdateObj.policyLocator}/renewals/${renewalUpdateObj.locator}/price-breakdown`,
			);
		}
	};

	const onIssue = async (isDraft?: boolean) => {
		let success = false;
		try {
			success = await issueRenewal(
				getValues(),
				dirtyFields,
				reset,
				{},
				isDraft,
			);
		} catch (e) {
			setLoading(false);
		}
		if (success) {
			navigate(
				`/policy/${renewalUpdateObj.policyLocator}/renewals/${renewalUpdateObj.locator}/price-breakdown`,
			);
		}
	};

	const onInvalidate = async () => {
		await invalidateRenewal();
		navigate(`/policy/${renewalUpdateObj.policyLocator}/renewals`);
	};

	const onDiscard = async () => {
		await discardRenewal();
		navigate(`/policy/${renewalUpdateObj.policyLocator}/renewals`);
	};

	const onClone = async () => {
		try {
			setLoading(true);
			const clonedRenewal = await cloneRenewal();
			successToast(RENEWAL_CLONE_SUCCESS());
			navigate(
				`/policy/${clonedRenewal.policyLocator}/renewals/${clonedRenewal.locator}`,
			);
		} catch (e) {
			const errorMessage = e?.response?.data?.message;

			if (CLONE_ERROR_REGEX.test(errorMessage)) {
				errorData.handleErrorResponse(e, RENEWAL_CLONE_CONFLICT_FAILURE());
			} else {
				errorData.handleErrorResponse(
					e,
					RENEWAL_CLONE_CONFLICT_UNKNOWN_FAILURE(),
				);
			}
		} finally {
			setLoading(false);
		}
	};

	const getCloneRenewalAction = (): HiddenActionItem => {
		if (state === 'application' && hasUnsavedChanges) {
			return {
				label: CLONE_RENEWAL_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						label={CLONE_RENEWAL_LABEL()}
						message={CLONE_RENEWAL_UNSAVED_CHANGES_MSG()}
						confirmText={CLONE_RENEWAL_UNSAVED_CHANGES_CONFIRM_TEXT()}
						cancelText={CLONE_RENEWAL_UNSAVED_CHANGES_CANCEL_TEXT()}
						dropdownItem
						onConfirm={onClone}
					/>
				),
			};
		}

		return {
			label: CLONE_RENEWAL_LABEL(),
			callback: onClone,
		};
	};

	const acceptMessage = willInvalidateOtherQuotes
		? ACCEPT_RENEWAL_CONFLICT_MSG('will')
		: mayInvalidateOtherQuotes
		? ACCEPT_RENEWAL_CONFLICT_MSG('may')
		: ACCEPT_RENEWAL_MSG();

	const invalidateMessage = willInvalidateOtherQuotes
		? INVALIDATE_RENEWAL_CONFLICT_MSG('will')
		: mayInvalidateOtherQuotes
		? INVALIDATE_RENEWAL_CONFLICT_MSG('may')
		: INVALIDATE_RENEWAL_MSG();

	const renderDraftActions = () => {
		const hiddenOptions = [];
		hasPermission('renewal.accept') &&
			hiddenOptions.push({
				label: ACCEPT_RENEWAL_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						label={ACCEPT_RENEWAL_LABEL()}
						message={acceptMessage}
						confirmText={ACCEPT_RENEWAL_LABEL()}
						dropdownItem
						onConfirm={() => onAccept(true)}
					>
						{willInvalidateOtherQuotes || mayInvalidateOtherQuotes
							? PROCEED_MSG()
							: null}
					</ActionConfirmationModal>
				),
			});
		hasPermission('renewal.issue') &&
			hiddenOptions.push({
				label: ISSUE_RENEWAL_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						label={ISSUE_RENEWAL_LABEL()}
						message={ISSUE_RENEWAL_MSG()}
						confirmText={ISSUE_RENEWAL_LABEL()}
						dropdownItem
						onConfirm={() => onIssue(true)}
					/>
				),
			});
		hasPermission('renewal.discard') &&
			hiddenOptions.push({
				label: DISCARD_RENEWAL_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						label={DISCARD_RENEWAL_LABEL()}
						message={DISCARD_RENEWAL_MSG()}
						confirmText={DISCARD_RENEWAL_LABEL()}
						dropdownItem
						onConfirm={onDiscard}
					/>
				),
			});
		hasPermission('renewal.create') &&
			hiddenOptions.push(getCloneRenewalAction());

		return (
			<Stack direction='row' spacing={1}>
				{hasPermission('renewal.update') && autofillEnabled ? (
					<RunAutofillButtonAndModal
						onConfirm={onRunAutofill}
						disabled={false}
					/>
				) : null}
				{hasPermission('renewal.update') && (
					<Button
						color='primary'
						variant='contained'
						disabled={!hasUnsavedChanges}
						onClick={onUpdate}
					>
						Save
					</Button>
				)}
				{hasPermission('renewal.quote') && (
					<ActionConfirmationModal
						label={QUOTE_RENEWAL_LABEL()}
						message={QUOTE_RENEWAL_MSG()}
						confirmText={QUOTE_RENEWAL_LABEL()}
						onConfirm={onQuote}
					/>
				)}
				<DropdownButton actions={hiddenOptions} />
			</Stack>
		);
	};

	const renderQuotedActions = () => {
		const hiddenOptions = [];
		hasPermission('renewal.issue') &&
			hiddenOptions.push({
				label: ISSUE_RENEWAL_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						dropdownItem
						label={ISSUE_RENEWAL_LABEL()}
						message={ISSUE_RENEWAL_MSG()}
						confirmText={ISSUE_RENEWAL_LABEL()}
						onConfirm={onIssue}
					/>
				),
			});
		hasPermission('renewal.invalidate') &&
			hiddenOptions.push({
				label: INVALIDATE_RENEWAL_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						dropdownItem
						label={INVALIDATE_RENEWAL_LABEL()}
						message={invalidateMessage}
						confirmText={INVALIDATE_RENEWAL_LABEL()}
						onConfirm={onInvalidate}
					>
						{willInvalidateOtherQuotes || mayInvalidateOtherQuotes
							? PROCEED_MSG()
							: null}
					</ActionConfirmationModal>
				),
			});
		hasPermission('renewal.discard') &&
			hiddenOptions.push({
				label: DISCARD_RENEWAL_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						dropdownItem
						label={DISCARD_RENEWAL_LABEL()}
						message={DISCARD_RENEWAL_MSG()}
						confirmText={DISCARD_RENEWAL_LABEL()}
						onConfirm={onDiscard}
					/>
				),
			});

		hasPermission('renewal.create') &&
			hiddenOptions.push(getCloneRenewalAction());

		return (
			<Stack direction='row' spacing={1}>
				{hasPermission('renewal.accept') && (
					<ActionConfirmationModal
						label={ACCEPT_RENEWAL_LABEL()}
						message={acceptMessage}
						confirmText={ACCEPT_RENEWAL_LABEL()}
						onConfirm={onAccept}
					>
						{willInvalidateOtherQuotes || mayInvalidateOtherQuotes
							? PROCEED_MSG()
							: null}
					</ActionConfirmationModal>
				)}

				<DropdownButton actions={hiddenOptions} />
			</Stack>
		);
	};

	const renderAcceptedActions = () => {
		const hiddenOptions = [];
		hasPermission('renewal.invalidate') &&
			hiddenOptions.push({
				label: INVALIDATE_RENEWAL_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						dropdownItem
						label={INVALIDATE_RENEWAL_LABEL()}
						message={invalidateMessage}
						confirmText={INVALIDATE_RENEWAL_LABEL()}
						onConfirm={onInvalidate}
					>
						{willInvalidateOtherQuotes || mayInvalidateOtherQuotes
							? PROCEED_MSG()
							: null}
					</ActionConfirmationModal>
				),
			});

		hasPermission('renewal.create') &&
			hiddenOptions.push(getCloneRenewalAction());

		return (
			<Stack direction='row' spacing={1}>
				{hasPermission('renewal.issue') && (
					<ActionConfirmationModal
						label={ISSUE_RENEWAL_LABEL()}
						message={ISSUE_RENEWAL_MSG()}
						confirmText={ISSUE_RENEWAL_LABEL()}
						onConfirm={onIssue}
					/>
				)}
				<DropdownButton actions={hiddenOptions} />
			</Stack>
		);
	};

	const renderDefaultActions = () => {
		const hiddenOptions = [];

		hasPermission('renewal.create') &&
			hiddenOptions.push(getCloneRenewalAction());

		return (
			<Stack direction='row' spacing={1}>
				<DropdownButton actions={hiddenOptions} />
			</Stack>
		);
	};

	if (!hasPermission('renewal.update')) {
		return null;
	}

	if (state === 'application') {
		return renderDraftActions();
	} else if (state === 'quoted') {
		return renderQuotedActions();
	} else if (state === 'accepted') {
		return renderAcceptedActions();
	} else {
		return renderDefaultActions();
	}
};

export default withRenewalContext<Props>(RenewalActions);
